/**
 * BinGrid - Responsive visual grid of bins
 *
 * Displays a grid of bins with proper responsive layout for
 * both mobile and large warehouse TV displays.
 *
 * Layout varies by bin size:
 * - STANDARD: 5 cols mobile, 10 cols desktop, gap-2
 * - LARGE: 4 cols mobile, 8 cols desktop, gap-2
 * - HUGE: 3 cols mobile, 6 cols desktop, gap-3
 * - BENCH (BLU-197): 2 cols mobile, 4 cols desktop, gap-3 — widest tier so
 *   work benches read clearly on the dashboard
 *
 * Empty state:
 * - Shows helpful icon and message when no bins configured
 * - Guides user to settings for bin configuration
 *
 * Loading state:
 * - BinGridSkeleton uses dynamic count from cached data
 * - Prevents jarring layout shifts when refreshing
 */
import { Box } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { BinCell, type BinCellProps } from './BinCell';

// Re-export the bin type from BinCell for convenience
type BinWithMetadata = BinCellProps['bin'];

type BinSize = 'STANDARD' | 'LARGE' | 'HUGE' | 'BENCH';

/** Grid layout classes by bin size */
const GRID_LAYOUT: Record<BinSize, string> = {
  STANDARD: 'grid-cols-5 md:grid-cols-10 gap-2',
  LARGE: 'grid-cols-4 md:grid-cols-8 gap-2',
  HUGE: 'grid-cols-3 md:grid-cols-6 gap-3',
  // BLU-197: work benches — 2 per row mobile, 4 per row desktop (wider than HUGE).
  BENCH: 'grid-cols-2 md:grid-cols-4 gap-3',
};

export interface BinGridProps {
  bins: BinWithMetadata[];
  binSize?: BinSize;
  compact?: boolean;
  className?: string;
}

/**
 * BinGrid component - Renders responsive visual grid of bins.
 *
 * @param bins - Array of bin data with status and metadata
 * @param binSize - Bin size controlling grid layout (default STANDARD)
 * @param compact - Optional compact mode for embedded views
 * @param className - Optional additional CSS classes
 */
export function BinGrid({ bins, binSize = 'STANDARD', compact = false, className }: BinGridProps) {
  const { t } = useTranslation('production');
  if (bins.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <Box className="h-12 w-12 mb-4 opacity-50" />
        <p className="text-lg font-medium">{t('bins.noBinsConfigured')}</p>
        <p className="text-sm">{t('bins.addBinsInSettings')}</p>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'grid',
        GRID_LAYOUT[binSize],
        className
      )}
      role="grid"
      aria-label={`${binSize.toLowerCase()} bin status grid`}
    >
      {bins.map(bin => (
        <BinCell key={bin.id} bin={bin} binSize={binSize} compact={compact} />
      ))}
    </div>
  );
}

export interface BinGridSkeletonProps {
  count?: number;
  binSize?: BinSize;
}

/**
 * BinGridSkeleton - Shows placeholder cells during loading.
 *
 * For better UX, pass the previous bin count from TanStack Query's cached data.
 * This prevents jarring layout shifts when refreshing (e.g., showing 10 skeletons
 * but then rendering 30 actual bins).
 *
 * @param count - Number of skeleton cells to show. Use previous data length when available.
 * @param binSize - Bin size for matching grid layout
 */
export function BinGridSkeleton({ count = 20, binSize = 'STANDARD' }: BinGridSkeletonProps) {
  // BLU-197: benches share the HUGE skeleton height (h-20).
  const heightClass = binSize === 'HUGE' || binSize === 'BENCH' ? 'h-20' : binSize === 'LARGE' ? 'h-[72px]' : 'h-14';

  return (
    <div
      className={cn('grid', GRID_LAYOUT[binSize])}
      role="grid"
      aria-label="Loading bin status"
      aria-busy="true"
    >
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className={cn(heightClass, 'rounded-lg bg-muted animate-pulse')}
          role="gridcell"
        />
      ))}
    </div>
  );
}
